<template>
  <div
    class="rounded-xl border p-3 flex gap-2.5 items-start"
    :class="issue.smsFallbackSent ? 'border-amber-200 bg-amber-50/70' : 'border-red-200 bg-red-50/70'"
  >
    <span
      class="w-8 h-8 rounded-lg flex items-center justify-center flex-none"
      :class="issue.smsFallbackSent ? 'bg-amber-100 text-amber-600' : 'bg-red-100 text-red-500'"
    >
      <i class="pi pi-whatsapp text-sm" />
    </span>
    <div class="flex flex-col gap-1 min-w-0 flex-1">
      <div class="flex items-center gap-2 flex-wrap">
        <span
          class="text-[13px] font-semibold"
          :class="issue.smsFallbackSent ? 'text-amber-700' : 'text-red-700'"
        >ההודעה בוואטסאפ לא נמסרה</span>
        <span
          v-if="messageLabel"
          class="text-[10.5px] px-1.5 py-0.5 rounded-full bg-white/80 border"
          :class="issue.smsFallbackSent ? 'text-amber-600 border-amber-200' : 'text-red-600 border-red-200'"
        >
          {{ messageLabel }}
        </span>
      </div>

      <div
        class="flex items-center gap-1.5 text-[11.5px] flex-wrap"
        :class="issue.smsFallbackSent ? 'text-amber-900/70' : 'text-red-900/70'"
      >
        <i class="pi pi-clock text-[10px]" />
        <span>{{ failedAtLabel }}</span>
      </div>

      <div
        class="mt-0.5 text-[12.5px] bg-white/70 border rounded-lg px-2.5 py-1.5 break-words"
        :class="issue.smsFallbackSent
          ? 'text-amber-900 border-amber-100'
          : 'text-red-900 border-red-100'"
      >
        <span class="font-medium">
          {{ issue.smsFallbackSent ? 'נשלחה הודעת SMS במקום.' : 'לא נשלחה הודעה חלופית ללקוח/ה.' }}
        </span>
        <span class="block mt-0.5 opacity-80">{{ reasonLabel }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AutomationTypeEnum, type INotificationDeliveryIssue } from '@torim-app/types';
import { computed } from 'vue';
import { DateTime } from 'luxon';

const props = defineProps<{ issue: INotificationDeliveryIssue }>();

// Which message was dropped — the owner's first question when they see this.
const MESSAGE_LABELS: Partial<Record<AutomationTypeEnum, string>> = {
  [AutomationTypeEnum.BookingConfirmation]: 'אישור תור',
  [AutomationTypeEnum.BookingCanceled]: 'ביטול תור',
  [AutomationTypeEnum.AppointmentRescheduled]: 'עדכון תור',
  [AutomationTypeEnum.AppointmentReminder]: 'תזכורת לתור',
  [AutomationTypeEnum.SameDayReminder]: 'תזכורת ביום התור',
};

// Meta's own wording is English and vague ("healthy ecosystem engagement"), so the
// codes worth explaining get a Hebrew explanation of what the owner can actually do.
const ERROR_EXPLANATIONS: Record<number, string> = {
  131049: 'וואטסאפ חסם את ההודעה כיוון שהלקוח/ה לא מגיב/ה להודעות מהעסק לאחרונה.',
  131047: 'לא ניתן לשלוח הודעה חופשית ללקוח/ה שלא כתב/ה לעסק ב-24 השעות האחרונות.',
  131026: 'לא ניתן לשלוח הודעה למספר הזה בוואטסאפ (ייתכן שאין לו/ה חשבון וואטסאפ).',
  131050: 'הלקוח/ה ביקש/ה להפסיק לקבל הודעות מהעסק בוואטסאפ.',
};

const messageLabel = computed(() =>
  props.issue.automationType
    ? MESSAGE_LABELS[props.issue.automationType as AutomationTypeEnum]
    : undefined
);

const reasonLabel = computed(() =>
  (props.issue.errorCode ? ERROR_EXPLANATIONS[props.issue.errorCode] : undefined)
  ?? props.issue.errorTitle
  ?? 'וואטסאפ לא מסר את ההודעה.'
);

const failedAtLabel = computed(() => {
  const dt = typeof props.issue.failedAt === 'string'
    ? DateTime.fromISO(props.issue.failedAt)
    : DateTime.fromJSDate(props.issue.failedAt as Date);
  if (!dt.isValid) return '';
  return dt.setLocale('he').toFormat('dd/LL/yyyy · HH:mm');
});
</script>

<style scoped lang="scss"></style>
